// src/components/chat/SelectedTextIndicator.tsx
import * as React from "react";

interface SelectedTextIndicatorProps {
  selectedText: string; // The actual text that was selected
  className?: string;
  compact?: boolean;
}

/**
 * Refined visual indicator for selected document text
 * Features sophisticated typography with large quotation marks and right-aligned attribution
 */
const SelectedTextIndicator: React.FC<SelectedTextIndicatorProps> = ({
  selectedText,
  className = "",
  compact = false,
}) => {
  // Icon representing text selection - keeping this for semantic clarity
  const SelectionIcon = () => (
    <svg
      width={compact ? "12" : "14"}
      height={compact ? "12" : "14"}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Text document with selection highlight - more intuitive icon */}
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.5" fill="none" />
      {/* Highlighted text lines */}
      <rect x="8" y="12" width="8" height="2" fill="currentColor" opacity="0.7" rx="1" />
      <rect x="8" y="15" width="6" height="2" fill="currentColor" opacity="0.7" rx="1" />
    </svg>
  );

  // Truncate selected text if it's too long
  const displayText =
    selectedText.length > 140 ? selectedText.substring(0, 140) + "..." : selectedText;

  // Main container - takes full available width for proper attribution alignment
  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%", // CHANGED: Take full width instead of fit-content
    maxWidth: "100%",
    marginBottom: "2px",
  };

  // Content preview container - focuses on the quoted text
  const contentPreviewContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start", // Align items to the top for proper quotation mark positioning
    gap: "8px", // Small gap between quotation mark and text
    marginBottom: "2px", // Space before the attribution line
    width: "100%", // Ensure content container takes full width
  };

  // Large opening quotation mark - creates visual emphasis and quote indication
  const openingQuoteStyle: React.CSSProperties = {
    color: "#414141",
    fontSize: "40px", // Large size for emphasis
    fontStyle: "italic",
    fontFamily: "Segoe UI", // Note: 'Segoe UI This' might not be available, using standard 'Segoe UI'
    fontWeight: "400",
    lineHeight: "45px",
    wordWrap: "break-word",
    flexShrink: 0, // Prevents the quote mark from shrinking
    alignSelf: "flex-start", // Ensures quote stays at the top
  };

  // Selected text content - readable and clean
  const selectedTextStyle: React.CSSProperties = {
    color: "#414141",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400",
    lineHeight: "21px",
    wordWrap: "break-word",
    flex: 1, // Takes remaining space after quotation mark
    paddingTop: "8px", // Slight offset to align with quote mark visually
  };

  // FIXED: Attribution container with explicit width for consistent right-alignment
  const attributionContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end", // Right-align the attribution
    alignItems: "center",
    gap: "6px",
    width: "100%", // Ensure attribution container takes full width
  };

  // Icon styling for the attribution line
  const iconStyle: React.CSSProperties = {
    color: "#898989", // Subtle color that doesn't compete with content
    flexShrink: 0,
  };

  // Attribution label styling - subtle and informative
  const labelStyle: React.CSSProperties = {
    color: "#898989",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400",
    lineHeight: "21px",
    wordWrap: "break-word",
  };

  return (
    <div className={className} style={containerStyle}>
      {/* Content preview with sophisticated typography */}
      <div style={contentPreviewContainerStyle}>
        {/* Large opening quotation mark for visual emphasis */}
        <span style={openingQuoteStyle}>"</span>

        {/* Selected text content without background - clean and readable */}
        <div style={selectedTextStyle}>{displayText}</div>
      </div>

      {/* Attribution line - right-aligned for visual balance */}
      <div style={attributionContainerStyle}>
        <div style={iconStyle}>
          <SelectionIcon />
        </div>
        <span style={labelStyle}>Highlighted from document</span>
      </div>
    </div>
  );
};

export default SelectedTextIndicator;
